import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { FFmpegCDNLoader } from './ffmpegCDNLoader';

/**
 * 视频帧提取器
 *
 * 抽取策略（按优先级）：
 * 1. Canvas 抽帧（主路径）：快、稳、零额外资源消耗；
 * 2. FFmpeg WASM 抽帧（兜底）：当 Canvas 因 CORS / codec 失败时使用。
 */
class VideoFrameExtractor {
  private ffmpeg: FFmpeg | null = null;
  private loadPromise: Promise<FFmpeg> | null = null;

  // ---------------- FFmpeg（兜底）----------------
  private async ensureFFmpegLoaded(): Promise<FFmpeg> {
    if (this.ffmpeg) return this.ffmpeg;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      const loader = new FFmpegCDNLoader();
      const { ffmpeg } = await loader.loadFFmpeg();
      ffmpeg.on('log', ({ message }) => {
        console.log('[VideoFrameExtractor:FFmpeg]', message);
      });
      this.ffmpeg = ffmpeg;
      return ffmpeg;
    })();

    try {
      return await this.loadPromise;
    } finally {
      this.loadPromise = null;
    }
  }

  private async extractByFFmpeg(videoUrl: string, timeSec: number): Promise<Blob> {
    console.log('[VideoFrameExtractor] 回退到 FFmpeg 抽帧', { videoUrl, timeSec });
    const ffmpeg = await this.ensureFFmpegLoaded();

    const prefix = `frame_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const inputName = `${prefix}_input.mp4`;
    const outputName = `${prefix}_frame.jpg`;

    try {
      const videoData = await fetchFile(videoUrl);
      if (!videoData || videoData.byteLength === 0) {
        throw new Error(`视频下载为空 byteLength=${videoData?.byteLength ?? 0}`);
      }
      await ffmpeg.writeFile(inputName, videoData);

      // 先读输入再 seek，兼容性最好；首帧取 0.0 秒
      await ffmpeg.exec([
        '-i', inputName,
        '-ss', timeSec.toString(),
        '-frames:v', '1',
        '-q:v', '2',
        '-f', 'image2',
        '-y',
        outputName,
      ]);

      const frameData = await ffmpeg.readFile(outputName);
      const uint8 = frameData as Uint8Array;
      if (!uint8 || uint8.length === 0) {
        throw new Error('FFmpeg 输出帧为空');
      }
      const copy = new Uint8Array(uint8.byteLength);
      copy.set(uint8);
      return new Blob([copy.buffer], { type: 'image/jpeg' });
    } finally {
      try { await ffmpeg.deleteFile(inputName); } catch { /* ignore */ }
      try { await ffmpeg.deleteFile(outputName); } catch { /* ignore */ }
    }
  }

  // ---------------- Canvas（主路径）----------------
  private extractByCanvas(videoUrl: string, timeSec: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';

      let settled = false;
      const cleanup = () => {
        video.removeAttribute('src');
        try { video.load(); } catch { /* ignore */ }
      };
      const fail = (err: unknown) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(err instanceof Error ? err : new Error(String(err)));
      };
      const succeed = (blob: Blob) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(blob);
      };

      const timeoutId = window.setTimeout(() => {
        fail(new Error('Canvas 抽帧超时 (30s)'));
      }, 30000);

      const doDraw = () => {
        try {
          const width = video.videoWidth;
          const height = video.videoHeight;
          if (!width || !height) {
            throw new Error('视频尺寸为 0，可能还未就绪');
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('无法获取 Canvas 2D 上下文');
          ctx.drawImage(video, 0, 0, width, height);
          canvas.toBlob((blob) => {
            window.clearTimeout(timeoutId);
            if (!blob || blob.size === 0) {
              fail(new Error('canvas.toBlob 返回空（可能被 CORS 污染）'));
              return;
            }
            succeed(blob);
          }, 'image/jpeg', 0.92);
        } catch (err) {
          window.clearTimeout(timeoutId);
          fail(err);
        }
      };

      video.addEventListener('loadeddata', () => {
        // loadeddata 表示已经准备好绘制第一帧
        if (timeSec <= 0) {
          doDraw();
        } else {
          // 需要 seek 到指定时间
          video.currentTime = Math.min(timeSec, Math.max(0, video.duration - 0.05));
        }
      });

      video.addEventListener('seeked', doDraw);

      video.addEventListener('error', () => {
        window.clearTimeout(timeoutId);
        const mediaError = video.error;
        fail(new Error(`video.error code=${mediaError?.code} message=${mediaError?.message || 'unknown'}`));
      });

      video.src = videoUrl;
    });
  }

  // ---------------- 对外 API ----------------
  /**
   * 从视频 URL 抽取一帧为 JPEG Blob
   *
   * @param videoUrl 视频 URL（跨域时服务端需允许 CORS）
   * @param timeSec  截取时间点（秒），默认 0（首帧）
   */
  async extractFrameAsBlob(videoUrl: string, timeSec: number = 0): Promise<Blob> {
    // 优先 Canvas 抽帧
    try {
      const blob = await this.extractByCanvas(videoUrl, timeSec);
      console.log('[VideoFrameExtractor] Canvas 抽帧成功', { videoUrl, timeSec, size: blob.size });
      return blob;
    } catch (err) {
      console.warn('[VideoFrameExtractor] Canvas 抽帧失败，回退到 FFmpeg:', err);
    }

    // 兜底 FFmpeg
    return this.extractByFFmpeg(videoUrl, timeSec);
  }

  /**
   * 抽帧并转换为 File，便于走 FormData 上传
   */
  async extractFrameAsFile(videoUrl: string, fileName: string = 'frame.jpg', timeSec: number = 0): Promise<File> {
    const blob = await this.extractFrameAsBlob(videoUrl, timeSec);
    return new File([blob], fileName, { type: 'image/jpeg' });
  }
}

export const videoFrameExtractor = new VideoFrameExtractor();
